import l from '../../../../lang';
import { functionalCurrenciesByCountry } from '../../../../config';
import {
  CreatePayoutData,
  FlowType,
  PayoutEnabledCountryConfiguration,
  PurposeOfPayout,
  RequiredField,
} from '../../../../store/payouts/interfaces';

const isCurrencyEnabledForFlow = (
  currency: PayoutEnabledCountryConfiguration['currencies'][number],
  flowType: FlowType
) => (flowType === FlowType.B2B ? currency.b2bEnabled : true);

export const getEnabledCountriesForFlow = (
  configurations: PayoutEnabledCountryConfiguration[],
  flowType: FlowType
) =>
  configurations
    .filter(config => config.currencies.some(currency => isCurrencyEnabledForFlow(currency, flowType)))
    .map(config => config.country)
    .sort();

export const getPayoutCountryOptions = (
  configurations: PayoutEnabledCountryConfiguration[],
  flowType: FlowType
): Record<string, string> =>
  getEnabledCountriesForFlow(configurations, flowType).reduce<Record<string, string>>((options, countryCode) => {
    options[countryCode] = l(`countryCode.${countryCode}`);
    return options;
  }, {});

export const getPayoutCurrencyOptions = (
  configurations: PayoutEnabledCountryConfiguration[],
  country: string,
  flowType: FlowType
): Record<string, string> => {
  const countryConfiguration = configurations.find(config => config.country === country);
  if (!countryConfiguration) {
    return {};
  }

  return countryConfiguration.currencies
    .filter(currency => isCurrencyEnabledForFlow(currency, flowType))
    .reduce<Record<string, string>>((options, currency) => {
      options[currency.currency] = currency.currency;
      return options;
    }, {});
};

export { recordToSelectMenuOptions } from '../../../../utils/common-utils';

export const payoutPurposeOptions: Partial<Record<PurposeOfPayout, string>> = {
  [PurposeOfPayout.MEDICAL_CARE]: l('payouts.create.purpose.medicalCare'),
  [PurposeOfPayout.TUITION_COSTS]: l('payouts.create.purpose.tuitionCosts'),
  [PurposeOfPayout.SHARES]: l('payouts.create.purpose.shares'),
  [PurposeOfPayout.INVESTMENT]: l('payouts.create.purpose.investment'),
  [PurposeOfPayout.UTILITY_PROVIDER]: l('payouts.create.purpose.utilityProvider'),
  [PurposeOfPayout.TAXES]: l('payouts.create.purpose.taxes'),
  [PurposeOfPayout.TOURISM_EXPENSE]: l('payouts.create.purpose.tourismExpense'),
  [PurposeOfPayout.TRAVEL]: l('payouts.create.purpose.travel'),
  [PurposeOfPayout.SAVINGS_RETIREMENT]: l('payouts.create.purpose.savingsRetirement'),
  [PurposeOfPayout.PENSION]: l('payouts.create.purpose.pension'),
  [PurposeOfPayout.PAYROLL]: l('payouts.create.purpose.payroll'),
  [PurposeOfPayout.PHYSICAL_GOODS]: l('payouts.create.purpose.physicalGoods'),
  [PurposeOfPayout.DIGITAL_GOODS]: l('payouts.create.purpose.digitalGoods'),
  [PurposeOfPayout.SUPPLIER]: l('payouts.create.purpose.supplier'),
  [PurposeOfPayout.CAPITAL_CONTRIBUTION]: l('payouts.create.purpose.capitalContribution'),
  [PurposeOfPayout.PROPERTY_RENTAL]: l('payouts.create.purpose.propertyRental'),
  [PurposeOfPayout.LOGISTICS]: l('payouts.create.purpose.logistics'),
  [PurposeOfPayout.INVESTMENT_INTEREST]: l('payouts.create.purpose.investmentInterest'),
  [PurposeOfPayout.AVAILABILITY]: l('payouts.create.purpose.availability'),
  [PurposeOfPayout.INSURANCE]: l('payouts.create.purpose.insurance'),
  [PurposeOfPayout.REWARDS]: l('payouts.create.purpose.rewards'),
  [PurposeOfPayout.ENTERTAINMENT]: l('payouts.create.purpose.entertainment'),
  [PurposeOfPayout.PUBLIC_SERVICES]: l('payouts.create.purpose.publicServices'),
  [PurposeOfPayout.AFFILIATION_PROGRAMS]: l('payouts.create.purpose.affiliationPrograms'),
  [PurposeOfPayout.OTHER_SERVICES]: l('payouts.create.purpose.otherServices'),
};

export const getPayoutWizardMenuItems = (activeStep: 'account' | 'payout' | 'review') => [
  {
    title: l('payouts.create.menu.accountData'),
    completed: activeStep === 'payout' || activeStep === 'review',
    selected: activeStep === 'account',
  },
  {
    title: l('payouts.create.menu.payoutData'),
    completed: activeStep === 'review',
    selected: activeStep === 'payout',
  },
  {
    title: l('payouts.create.menu.review'),
    completed: false,
    selected: activeStep === 'review',
  },
];

export const getFlowTypeLabel = (flowType?: FlowType) => {
  if (flowType === FlowType.B2B) {
    return l('payouts.create.flow.business');
  }
  return l('payouts.create.flow.person');
};

export const getFieldLabel = (field: RequiredField) => {
  const map: Record<string, string> = {
    'beneficiary.first_name': l('payouts.create.field.firstName'),
    'beneficiary.last_name': l('payouts.create.field.lastName'),
    'beneficiary.email': l('payouts.create.field.email'),
    'beneficiary.relationship': l('payoutContacts.detail.relationship'),
    purpose: l('payouts.create.field.purpose'),
    'beneficiary.bank_account.code': l('payouts.create.field.bankName'),
    'beneficiary.bank_account.type': l('payouts.create.field.accountType'),
    'beneficiary.bank_account.account': l('payouts.create.field.accountNumber'),
    'beneficiary.document.id': l('payouts.create.field.document'),
    'beneficiary.document.type': l('payouts.create.field.documentType'),
    'beneficiary.bank_account.branch': l('payouts.create.field.bankBranch'),
    'beneficiary.address.street': l('payouts.create.field.beneficiaryAddress'),
    'beneficiary.business_name': l('payouts.create.field.businessName'),
    'beneficiary.address.city': l('payouts.create.field.beneficiaryAddressCity'),
    'remitter.first_name': l('payouts.create.field.remitterFirstName'),
    'remitter.last_name': l('payouts.create.field.remitterLastName'),
    'remitter.document.id': l('payouts.create.field.remitterDocument'),
    'remitter.nationality': l('payouts.create.field.remitterNationality'),
    'remitter.birth_info.date': l('payouts.create.field.remitterBirthDate'),
  };

  if (map[field.fieldPath]) {
    return map[field.fieldPath];
  }

  const fallback = field.fieldPath.split('.').pop() || field.fieldPath;
  return fallback.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
};

const fieldPathToCreatePayoutDataKeyMap: Record<string, keyof CreatePayoutData> = {
  amount: 'transferAmount',
  country: 'transferCountry',
  flow_type: 'flowType',
  purpose: 'purpose',
  'beneficiary.purpose': 'purpose',
  'beneficiary.first_name': 'beneficiaryFirstName',
  'beneficiary.last_name': 'beneficiaryLastName',
  'beneficiary.business_name': 'businessName',
  'beneficiary.document.id': 'beneficiaryDocument',
  'beneficiary.document.type': 'beneficiaryDocumentType',
  'beneficiary.address.street': 'beneficiaryAddressStreet',
  'beneficiary.address.city': 'beneficiaryAddressCity',
  'beneficiary.bank_account.code': 'bankCode',
  'beneficiary.bank_name': 'bankCode',
  'beneficiary.bank_account.account': 'bankAccount',
  'beneficiary.bank_account.branch': 'bankBranch',
  'beneficiary.bank_account.type': 'bankAccountType',
  currency_to_pay: 'currencyToPay',
  description: 'description',
  notification_url: 'notificationUrl',
};

export const getCreatePayoutDataValueByFieldPath = (createPayoutData: Partial<CreatePayoutData>, fieldPath: string) => {
  const key = fieldPathToCreatePayoutDataKeyMap[fieldPath];
  if (!key) {
    return undefined;
  }
  return createPayoutData[key];
};

export const mapFieldPathValuesToCreatePayoutData = (values: Record<string, string | number>) => {
  return Object.entries(values).reduce<Partial<CreatePayoutData>>((accumulator, [fieldPath, value]) => {
    const key = fieldPathToCreatePayoutDataKeyMap[fieldPath];
    if (key) {
      accumulator[key] = value as never;
    }
    return accumulator;
  }, {});
};

const createPayoutDataKeyToFieldPathMap = Object.entries(fieldPathToCreatePayoutDataKeyMap).reduce<
  Partial<Record<keyof CreatePayoutData, string>>
>((accumulator, [fieldPath, key]) => {
  if (!accumulator[key]) {
    accumulator[key] = fieldPath;
  }
  return accumulator;
}, {});

export const mapCreatePayoutDataToFieldPathValues = (createPayoutData: Partial<CreatePayoutData>) => {
  return Object.entries(createPayoutData).reduce<Record<string, string | number>>((accumulator, [rawKey, value]) => {
    if (value === undefined) {
      return accumulator;
    }

    const key = rawKey as keyof CreatePayoutData;
    const fieldPath = createPayoutDataKeyToFieldPathMap[key];
    if (fieldPath) {
      accumulator[fieldPath] = value as string | number;
    }
    return accumulator;
  }, {});
};

export const getBankDisplayName = (
  bankCode: string | number | undefined,
  bankOptions: Array<{ code: string; name: string }>
) => {
  if (bankCode === undefined || bankCode === null || bankCode === '') {
    return '-';
  }

  const bank = bankOptions.find(option => String(option.code) === String(bankCode));
  return bank?.name || String(bankCode);
};

const accountTypeDisplayLabels: Record<string, string> = {
  CHECKING: 'CHECKING',
  SAVINGS: 'SAVINGS',
  SALARY: 'SALARY',
  VISTA: 'VISTA',
  MASTER: 'MASTER',
  MAESTRA: 'MAESTRA',
  ALIAS: 'ALIAS',
  CBU: 'CBU/CVU',
};

export const getAccountTypeDisplayLabel = (accountType?: string) => {
  if (!accountType) {
    return '-';
  }

  return accountTypeDisplayLabels[accountType] || accountType;
};

export const getBeneficiaryDisplayName = (createPayoutData: Partial<CreatePayoutData>) => {
  if (createPayoutData.flowType === FlowType.B2B) {
    return String(createPayoutData.businessName || '-').trim() || '-';
  }

  const firstName = String(createPayoutData.beneficiaryFirstName || '').trim();
  const lastName = String(createPayoutData.beneficiaryLastName || '').trim();
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || '-';
};

export const getCurrencyFlagCountryCode = (currency: string, transferCountry: string) => {
  if (currency === 'USD') {
    return 'US';
  }

  const localCurrency = functionalCurrenciesByCountry[transferCountry];
  if (localCurrency === currency) {
    return transferCountry;
  }

  return transferCountry;
};

export const applyPayoutAmountToFixed = (value: number | string) => {
  const numericValue = Number(value);
  return numericValue && numericValue !== 0 ? numericValue.toFixed(2) : value;
};
